import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { ICampaignAnalyticsFilter } from '@pages/campaigns/campaign-details/campaign-details.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { DatePickerUtilitiesService } from '@utilities/date-picker-utilities.service';
import { differenceInDays, endOfDay, isAfter, isBefore, startOfDay, subDays } from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  ECompareRangeType,
  EDateRangeType,
  ICompareRange,
  IDateRangeBounds,
  IProfileDateFilterState,
} from './profile-date-filter.interface';
import { calculatePredefinedDateRange } from './profile-date-filter.util';

@Component({
  imports: [
    DatePipe,
    IconComponent,
    MatErrorComponent,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
    MatTooltipModule,
    TextClampTooltipDirective,
  ],
  providers: [provideNativeDateAdapter()],
  selector: 'app-profile-date-filter',
  styleUrl: './profile-date-filter.component.scss',
  templateUrl: './profile-date-filter.component.html',
})
export class ProfileDateFilterComponent implements OnInit {
  public readonly campaignAnalytics = input.required<ICampaignAnalytics | null>();

  public readonly loading = input<boolean>(false);
  public readonly hideAllTimeRangeOption = input<boolean>(false);
  public readonly initialState = input<IProfileDateFilterState | null>(null);

  /**
   * When set, restricts selectable dates (calendar min/max) to these bounds and hides any
   * predefined option whose calculated range doesn't fully fit inside them. When null (default),
   * the picker behaves exactly as before (unrestricted, only allTime gets clamped to the campaign).
   */
  public readonly allowedDateRange = input<IDateRangeBounds | null>(null);
  /** Hides This week/This month/Last 7 days/Last 30 days regardless of allowedDateRange fit. */
  public readonly hideRelativeRangeOptions = input<boolean>(false);
  /** Hides these specific predefined ranges (the "custom" option is never hidden by this). */
  public readonly excludedRangeTypes = input<EDateRangeType[]>([]);

  /**
   * Base i18n key under which this component looks up its predefined-range labels
   * (`${translationKeyPrefix}.timeRange`, `${translationKeyPrefix}.<range>`, etc). Lets callers
   * outside the campaign/creator/customer profile context (e.g. admin pages) supply their own
   * copy - most notably a non-campaign-specific label for the "allTime" option.
   */
  public readonly translationKeyPrefix = input<string>('pages.campaignDetails.filter');

  /**
   * Fallback bounds used to resolve the "allTime" option when there's no `campaignAnalytics`
   * (e.g. admin pages with no single campaign to scope to). Ignored whenever `campaignAnalytics`
   * is set - that always takes precedence, exactly like before this input existed.
   */
  public readonly allTimeBounds = input<IDateRangeBounds | null>(null);

  /** Shows the "Compare" section (previous period / custom range comparison) below the date range. */
  public readonly showCompare = input<boolean>(false);

  // Exposed as plain Date | null (rather than inlining `allowedDateRange()?.start` in the
  // template) so Angular's datepicker min/max generic type inference doesn't widen to
  // `Date | undefined`, which conflicts with the MatDatepicker<Date> view child type.
  public readonly allowedMinDate = computed<Date | null>(() => this.allowedDateRange()?.start ?? null);
  public readonly allowedMaxDate = computed<Date | null>(() => this.allowedDateRange()?.end ?? null);

  // The end datepicker can't also take a [max] binding (see onEndDateChange below), so the
  // calendar cells past the allowed bound are disabled via a filter predicate instead.
  public readonly endDateFilter = (date: Date | null): boolean => {
    const maxDate = this.allowedMaxDate();

    return !date || !maxDate || !isAfter(date, endOfDay(maxDate));
  };

  public readonly dateRangeChanged = output<Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'>>();
  public readonly selectedRangeChanged = output<EDateRangeType>();
  /** Emits the resolved compare range, or `null` when comparison is off (mode is "none" / disabled). */
  public readonly compareRangeChanged = output<ICompareRange | null>();

  public readonly selectedRange = signal<EDateRangeType>(EDateRangeType.allTime);
  public readonly startDate = signal<Date | null>(null);
  public readonly endDate = signal<Date | null>(null);

  public readonly form = new FormGroup({
    endDate: new FormControl<Date | null>(null),
    startDate: new FormControl<Date | null>(null),
  });

  public readonly EDateRangeType = EDateRangeType;
  public readonly predefinedRanges = Object.values(EDateRangeType);

  public readonly visiblePredefinedRanges = computed<Set<EDateRangeType>>(() => {
    const bounds = this.allowedDateRange();
    const hideAllTime = this.hideAllTimeRangeOption();
    const hideRelative = this.hideRelativeRangeOptions();
    const excluded = new Set(this.excludedRangeTypes());

    return new Set(
      this.predefinedRanges.filter((range) => {
        if (range === EDateRangeType.custom) {
          return true;
        }

        if (excluded.has(range)) {
          return false;
        }

        if (range === EDateRangeType.allTime) {
          if (hideAllTime) {
            return false;
          }
        } else if (hideRelative) {
          return false;
        }

        if (!bounds) {
          return true;
        }

        const { end, start } = this.calculatePredefinedRange(range);

        return !isBefore(start, startOfDay(bounds.start)) && !isBefore(endOfDay(bounds.end), end);
      }),
    );
  });

  /** Resolved bounds for the "allTime" option: the campaign's own range when scoped to one, else `allTimeBounds`. */
  public readonly effectiveAllTimeBounds = computed<IDateRangeBounds | null>(() => {
    const campaignAnalytics = this.campaignAnalytics();

    if (campaignAnalytics) {
      return { end: campaignAnalytics.end, start: campaignAnalytics.start };
    }

    return this.allTimeBounds();
  });

  public readonly ECompareRangeType = ECompareRangeType;
  public readonly compareRangeOptions = Object.values(ECompareRangeType);

  public readonly compareRangeType = signal<ECompareRangeType>(ECompareRangeType.none);
  public readonly compareStartDate = signal<Date | null>(null);
  public readonly compareEndDate = signal<Date | null>(null);

  /** "Full period" has no meaningful "period before it" to compare against, so Compare is disabled then. */
  public readonly compareDisabled = computed<boolean>(() => this.selectedRange() === EDateRangeType.allTime);

  public readonly compareForm = new FormGroup({
    endDate: new FormControl<Date | null>(null),
    startDate: new FormControl<Date | null>(null),
  });

  private readonly startRangePicker = viewChild.required<MatDateRangePicker<Date>>('startRangePicker');
  private readonly endDatePicker = viewChild.required<MatDatepicker<Date>>('endDatePicker');

  // Optional (not `.required`) because the Compare section - and these pickers with it - only
  // renders at all when `showCompare()` is true.
  private readonly compareStartRangePicker = viewChild<MatDateRangePicker<Date>>('compareStartRangePicker');
  private readonly compareEndDatePicker = viewChild<MatDatepicker<Date>>('compareEndDatePicker');

  public readonly datePickerUtilities = inject(DatePickerUtilitiesService);
  private readonly destroyRef = inject(DestroyRef);

  private isSyncingPicker = false;
  private readonly isSettingPredefinedRange = signal<boolean>(false);
  private shouldAutoClampInitialAllTime = true;
  private pendingAllTimeClampEmission = false;
  private dateRangeIsClamped = false;
  /** Tracks whether the predefined-range menu is currently open - see `setupInitialDateRangeClampEffect`. */
  private isMenuOpen = false;
  private lastEmittedDateRange: Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'> | null = null;
  private lastEmittedCompareRange: ICompareRange | null = null;
  // Guards `setupFormSync`'s own programmatic writes (and whatever they synchronously cascade
  // into - see below) from being mistaken by `setupFormValueChanges` for a real form/user-driven
  // change.
  private isSyncingFormFromSignals = false;
  // Same hazard as `isSyncingFormFromSignals`, but for `compareForm`'s linked start/end pair -
  // guards `applyPreviousPeriodCompareRange`'s programmatic patch from being mistaken by
  // `setupCompareFormValueChanges` for a real form/user-driven change.
  private isSyncingCompareFormFromSignals = false;

  constructor() {
    this.initializeDefaultRange();
    this.setupFormSync();
    this.setupAllTimeRangeSyncEffect();
    this.setupInitialDateRangeClampEffect();
    this.setupCompareStateEffect();
    this.setupCompareFormDisabledEffect();
  }

  public ngOnInit(): void {
    this.initializeFromStateOrDefault();
    this.setupPickerSubscriptions();
    this.setupFormValueChanges();
    this.setupCompareFormValueChanges();
  }

  public onPredefinedRangeSelect(range: EDateRangeType, event: Event): void {
    event.stopImmediatePropagation();

    this.setSelectedRange(range);

    // Only defer to `setupInitialDateRangeClampEffect` when the real bounds genuinely aren't
    // resolved yet (the campaign-details page's `campaignAnalytics` can still be loading async).
    // Checking `!campaignAnalytics()` alone used to also catch admin-style usages that pass a
    // plain `allTimeBounds` input instead (there's no campaign to scope to) - those bounds are
    // available synchronously, so this flag was getting set on every "Full period" click even
    // though nothing was actually pending, which made the effect fire `emitDateRange()`
    // immediately instead of waiting for the menu to close like every other predefined range.
    if (range === EDateRangeType.allTime && !this.effectiveAllTimeBounds()) {
      this.pendingAllTimeClampEmission = true;
    }

    if (range !== EDateRangeType.allTime) {
      this.pendingAllTimeClampEmission = false;
    }

    if (range === EDateRangeType.custom) {
      return;
    }

    this.isSettingPredefinedRange.set(true);

    const { end, start } = this.calculatePredefinedRange(range);
    let nextStartDate = start;
    let nextEndDate = end;

    if (range === EDateRangeType.allTime) {
      const clampedDateRange = this.clampDateRangeToCampaign(start, end);
      const { endDate, startDate } = clampedDateRange;

      if (startDate && endDate) {
        nextStartDate = startDate;
        nextEndDate = endDate;
      }
    }

    this.startDate.set(nextStartDate);
    this.endDate.set(nextEndDate);

    // See `setupFormSync` - guards this combined patch (and whatever it synchronously cascades
    // into via Angular Material's linked start/end date-range inputs) from being mistaken by
    // `setupFormValueChanges` for a real form/user-driven change. Missing this guard here let a
    // stale cross-notify echo overwrite `startDate`/`endDate` with pre-update values right after
    // a predefined range was (re-)selected - most visibly when "Full period" is re-picked during
    // the lock window right before `campaignAnalytics` delivers fresher bounds.
    this.isSyncingFormFromSignals = true;

    try {
      this.form.patchValue(
        {
          endDate: nextEndDate,
          startDate: nextStartDate,
        },
        { emitEvent: false },
      );
    } finally {
      this.isSyncingFormFromSignals = false;
    }

    this.syncPickerSelections();

    setTimeout(() => {
      this.isSettingPredefinedRange.set(false);
    });
  }

  public openStartPicker(event: Event): void {
    event.stopImmediatePropagation();

    if (this.endDatePicker()) {
      this.endDatePicker().close();
    }

    requestAnimationFrame(() => {
      this.syncPickerSelections();
      this.startRangePicker().open();
    });
  }

  public openEndPicker(event: Event): void {
    event.stopImmediatePropagation();

    if (this.startRangePicker()) {
      this.startRangePicker().close();
    }

    requestAnimationFrame(() => {
      this.syncPickerSelections();
      this.endDatePicker().open();
    });
  }

  public onMenuOpened(): void {
    this.isMenuOpen = true;
  }

  public onMenuClosed(): void {
    this.isMenuOpen = false;
    this.emitDateRange();
    this.emitCompareRange();
  }

  public onCompareRangeSelect(mode: ECompareRangeType, event: Event): void {
    event.stopImmediatePropagation();

    if (this.compareDisabled()) {
      return;
    }

    this.compareRangeType.set(mode);

    if (mode === ECompareRangeType.previousPeriod) {
      this.applyPreviousPeriodCompareRange();
    } else if (mode === ECompareRangeType.none) {
      this.compareStartDate.set(null);
      this.compareEndDate.set(null);
      this.compareForm.patchValue({ endDate: null, startDate: null });
    }
  }

  public openCompareStartPicker(event: Event): void {
    event.stopImmediatePropagation();

    if (this.compareDisabled() || this.compareRangeType() !== ECompareRangeType.custom) {
      return;
    }

    requestAnimationFrame(() => {
      this.compareStartRangePicker()?.open();
    });
  }

  public openCompareEndPicker(event: Event): void {
    event.stopImmediatePropagation();

    if (this.compareDisabled() || this.compareRangeType() !== ECompareRangeType.custom) {
      return;
    }

    requestAnimationFrame(() => {
      this.compareEndDatePicker()?.open();
    });
  }

  private initializeDefaultRange(): void {
    this.startDate.set(null);
    this.endDate.set(null);
    this.form.patchValue({ endDate: null, startDate: null });
    this.lastEmittedDateRange = { endDate: null, startDate: null };
  }

  private initializeFromStateOrDefault(): void {
    if (this.applyInitialState()) {
      return;
    }

    this.setDefaultRangeForHiddenAllTimeOption();
  }

  private applyInitialState(): boolean {
    const initialState: IProfileDateFilterState | null = this.initialState();

    if (!initialState) {
      return false;
    }

    const { endDate, selectedRange: initialSelectedRange, startDate } = initialState;
    let selectedRange = initialSelectedRange;

    if (this.hideAllTimeRangeOption() && selectedRange === EDateRangeType.allTime) {
      selectedRange = EDateRangeType.last30Days;
    }

    if (selectedRange !== EDateRangeType.custom && (startDate === null || endDate === null)) {
      return false;
    }

    this.isSettingPredefinedRange.set(true);
    this.shouldAutoClampInitialAllTime = selectedRange === EDateRangeType.allTime;
    this.setSelectedRange(selectedRange, false);

    if (selectedRange === EDateRangeType.custom) {
      this.startDate.set(startDate);
      this.endDate.set(endDate);
      this.form.patchValue({
        endDate,
        startDate,
      });
      this.lastEmittedDateRange = { endDate, startDate };
    } else if (selectedRange === EDateRangeType.allTime) {
      // Normalize/clamp exactly like `calculatePredefinedRange`/`clampDateRangeToCampaign` do
      // elsewhere, so this initial value is byte-for-byte what `setupInitialDateRangeClampEffect`
      // and `setupAllTimeRangeSyncEffect` would independently compute from this same
      // `effectiveAllTimeBounds()`. Storing the caller's raw (un-normalized) `startDate`/`endDate`
      // verbatim here - as this used to - left the signals holding e.g. a creator's exact
      // `createdAt` time-of-day, while those two effects, finding a mismatch against their own
      // startOfDay/endOfDay-rounded target on the very next tick, silently overwrote the signals
      // to the rounded values WITHOUT updating `lastEmittedDateRange` to match (they only emit
      // when correcting a genuine async-arrived bounds change, not this synchronous initial one).
      // That desync meant the *next* `onMenuClosed` - even one where the user picked nothing at
      // all - saw current (rounded) !== lastEmitted (raw) and spuriously fired `dateRangeChanged`
      // with different bounds than the page's very first fetch used, causing an unnecessary
      // refetch with a shifted range on a plain open-then-close-without-selecting.
      const { end: normalizedEnd, start: normalizedStart } = this.calculatePredefinedRange(EDateRangeType.allTime);
      const clampedDateRange = this.clampDateRangeToCampaign(normalizedStart, normalizedEnd);
      const finalStartDate = clampedDateRange.startDate ?? normalizedStart;
      const finalEndDate = clampedDateRange.endDate ?? normalizedEnd;

      this.startDate.set(finalStartDate);
      this.endDate.set(finalEndDate);
      this.form.patchValue({
        endDate: finalEndDate,
        startDate: finalStartDate,
      });
      this.lastEmittedDateRange = { endDate: finalEndDate, startDate: finalStartDate };
      this.dateRangeIsClamped = true;
    } else {
      const { end, start } = this.calculatePredefinedRange(selectedRange);

      this.startDate.set(start);
      this.endDate.set(end);
      this.form.patchValue({
        endDate: end,
        startDate: start,
      });
      this.lastEmittedDateRange = { endDate: end, startDate: start };
    }

    setTimeout(() => {
      this.isSettingPredefinedRange.set(false);
    });

    return true;
  }

  private setDefaultRangeForHiddenAllTimeOption(): void {
    if (!this.hideAllTimeRangeOption()) {
      return;
    }

    this.isSettingPredefinedRange.set(true);
    this.shouldAutoClampInitialAllTime = false;
    this.setSelectedRange(EDateRangeType.last30Days, false);

    const { end, start } = this.calculatePredefinedRange(EDateRangeType.last30Days);

    this.startDate.set(start);
    this.endDate.set(end);
    this.form.patchValue({
      endDate: end,
      startDate: start,
    });
    this.lastEmittedDateRange = { endDate: end, startDate: start };

    setTimeout(() => {
      this.isSettingPredefinedRange.set(false);
    });
  }

  private setupFormSync(): void {
    effect(() => {
      const startDate = this.startDate();
      const endDate = this.endDate();

      // `{ emitEvent: false }` only suppresses *this* control's own valueChanges/parent update -
      // it does NOT stop Angular Material's linked start/end date-range inputs from cross-
      // notifying each other's registered validator-change callback as part of writeValue (this
      // is how the range picker keeps both inputs' validity in sync). That cross-notification
      // calls `updateValueAndValidity()` on the *other* control with its default options
      // (emitEvent: true), which re-emits that control's valueChanges carrying whatever value it
      // still happens to hold at that exact synchronous instant - which can be stale if this
      // effect hasn't patched it yet. Without this guard, that stale echo gets read by
      // `setupFormValueChanges`'s subscribers and written back into the signals, undoing the very
      // update this effect is trying to apply. The guard covers the whole synchronous call below,
      // including any such cascade, since effects and this cross-notification chain are all
      // synchronous.
      this.isSyncingFormFromSignals = true;

      try {
        if (startDate !== this.form.get('startDate')?.value) {
          this.form.patchValue({ startDate }, { emitEvent: false });
        }

        if (endDate !== this.form.get('endDate')?.value) {
          this.form.patchValue({ endDate }, { emitEvent: false });
        }
      } finally {
        this.isSyncingFormFromSignals = false;
      }
    });
  }

  private setupPickerSubscriptions(): void {
    if (this.startRangePicker()) {
      this.startRangePicker()
        .closedStream.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.ensureEndDateWhenOnlyStartSelected();

          requestAnimationFrame(() => {
            this.syncPickerSelections();
          });
        });
    }

    if (this.endDatePicker()) {
      this.endDatePicker()
        .closedStream.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.ensureEndDateWhenOnlyStartSelected();

          requestAnimationFrame(() => {
            this.syncPickerSelections();
          });
        });
    }

    this.compareStartRangePicker()
      ?.closedStream.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.ensureCompareEndDateWhenOnlyStartSelected();
      });

    this.compareEndDatePicker()
      ?.closedStream.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.ensureCompareEndDateWhenOnlyStartSelected();
      });
  }

  private ensureCompareEndDateWhenOnlyStartSelected(): void {
    const startDate = (this.compareForm.get('startDate')?.value as Date | null) || null;
    const endDate = (this.compareForm.get('endDate')?.value as Date | null) || null;

    if (startDate && (!endDate || isBefore(startOfDay(endDate), startOfDay(startDate)))) {
      this.compareForm.get('endDate')?.setValue(endOfDay(startDate));
    }
  }

  private setupFormValueChanges(): void {
    this.form
      .get('startDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // See `setupFormSync` - a value change here can be a stale echo cascaded from Angular
        // Material's linked start/end date-range inputs cross-validating each other during our own
        // programmatic signal->form sync, not a real form/user-driven change.
        if (this.isSyncingFormFromSignals) {
          return;
        }

        const isUserEdit = !this.isSettingPredefinedRange();

        if (value && this.selectedRange() !== EDateRangeType.custom && isUserEdit) {
          this.setSelectedRange(EDateRangeType.custom);
        }

        this.startDate.set(value);
      });

    this.form
      .get('endDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // See `setupFormSync` - same stale-echo hazard as the startDate subscriber above.
        if (this.isSyncingFormFromSignals) {
          return;
        }

        const isUserEdit = !this.isSettingPredefinedRange();
        const normalizedEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
          value as Date | null,
          (this.form.get('startDate')?.value as Date | null) ?? null,
        );

        if (value && normalizedEndDate && value.getTime() !== normalizedEndDate.getTime()) {
          this.form.get('endDate')?.setValue(normalizedEndDate, { emitEvent: false });
        }

        if (value && this.selectedRange() !== EDateRangeType.custom && isUserEdit) {
          this.setSelectedRange(EDateRangeType.custom);
        }

        this.endDate.set(normalizedEndDate);

        if (isUserEdit) {
          this.emitDateRange();
        }
      });
  }

  private calculatePredefinedRange(range: EDateRangeType): IDateRangeBounds {
    return calculatePredefinedDateRange(range, this.effectiveAllTimeBounds());
  }

  private syncPickerSelections(): void {
    if (this.isSyncingPicker) {
      return;
    }

    const startPicker = this.startRangePicker();
    const endPicker = this.endDatePicker();

    if (startPicker?.opened || endPicker?.opened) {
      return;
    }

    this.isSyncingPicker = true;

    const wasSettingPredefinedRange = this.isSettingPredefinedRange();

    setTimeout(() => {
      if (wasSettingPredefinedRange && !this.isSettingPredefinedRange()) {
        this.isSyncingPicker = false;

        return;
      }

      const latestStartDate = this.startDate();
      const latestEndDate = this.endDate();

      // See `setupFormSync` - `picker.select()` on a linked Material date-range input can cross-
      // trigger the *other* control's validator-change callback the same way a raw `patchValue`
      // does, calling `updateValueAndValidity()` on it with default options and echoing its still-
      // stale value back out through `valueChanges`. Guard the whole block (picker selection AND
      // the form `setValue` calls below) so `setupFormValueChanges`'s subscribers ignore any such
      // echo instead of writing it back into the `startDate`/`endDate` signals - without this,
      // that echo re-arms every effect reading those signals, which can re-trigger this same sync
      // and never converge.
      this.isSyncingFormFromSignals = true;

      try {
        if (startPicker && !startPicker.opened) {
          if (latestStartDate) {
            startPicker.select(latestStartDate);
          }

          if (latestEndDate) {
            startPicker.select(latestEndDate);
          }
        }

        if (endPicker && !endPicker.opened && latestEndDate) {
          endPicker.select(latestEndDate);
        }

        this.form.controls.startDate.setValue(latestStartDate, { emitEvent: false });
        this.form.controls.endDate.setValue(latestEndDate, { emitEvent: false });
      } finally {
        this.isSyncingFormFromSignals = false;
      }

      this.isSyncingPicker = false;
    });
  }

  private emitDateRange(): void {
    const endDate = this.endDate();
    const startDate = this.startDate();

    // Compared by timestamp, not object identity - a spurious re-select of an unchanged date
    // (e.g. syncPickerSelections() re-selecting the end date picker's already-set value right
    // after it's opened) produces a new Date instance with the same time, which reference
    // equality would miss and double-emit as if the range had actually changed.
    if (
      endDate?.getTime() === this.lastEmittedDateRange?.endDate?.getTime() &&
      startDate?.getTime() === this.lastEmittedDateRange?.startDate?.getTime()
    ) {
      return;
    }

    if (!startDate || !endDate) {
      this.lastEmittedDateRange = { endDate, startDate };

      return;
    }

    let emittedStartDate = startOfDay(startDate);
    let emittedEndDate = endOfDay(endDate);

    if (this.selectedRange() === EDateRangeType.allTime) {
      const clampedDateRange = this.clampDateRangeToCampaign(emittedStartDate, emittedEndDate);

      if (!clampedDateRange.startDate || !clampedDateRange.endDate) {
        return;
      }

      emittedStartDate = startOfDay(clampedDateRange.startDate);
      emittedEndDate = endOfDay(clampedDateRange.endDate);
    }

    // Tracked using the actual emitted (normalized/clamped) values, not the raw pre-normalization
    // signal values read above - otherwise, once the "if" block below corrects startDate/endDate
    // to these normalized values, every subsequent call would compare the (now-normalized)
    // signals against this stale raw snapshot, never match, and re-emit on every single call.
    this.lastEmittedDateRange = { endDate: emittedEndDate, startDate: emittedStartDate };

    if (startDate.getTime() !== emittedStartDate.getTime() || endDate.getTime() !== emittedEndDate.getTime()) {
      this.isSettingPredefinedRange.set(true);
      this.startDate.set(emittedStartDate);
      this.endDate.set(emittedEndDate);

      // See `setupFormSync` - guards this combined patch (and whatever it synchronously cascades
      // into via Angular Material's linked start/end date-range inputs) from being mistaken by
      // `setupFormValueChanges` for a real form/user-driven change.
      this.isSyncingFormFromSignals = true;

      try {
        this.form.patchValue(
          {
            endDate: emittedEndDate,
            startDate: emittedStartDate,
          },
          { emitEvent: false },
        );
      } finally {
        this.isSyncingFormFromSignals = false;
      }

      this.syncPickerSelections();

      setTimeout(() => {
        this.isSettingPredefinedRange.set(false);
      });
    }

    this.dateRangeChanged.emit({
      endDate: emittedEndDate,
      startDate: emittedStartDate,
    });
  }

  private ensureEndDateWhenOnlyStartSelected(): void {
    const startDate = (this.form.get('startDate')?.value as Date | null) || null;
    const endDate = (this.form.get('endDate')?.value as Date | null) || null;

    if (startDate && !endDate) {
      const endOfStartDate = endOfDay(startDate);
      this.form.get('endDate')?.setValue(endOfStartDate);
      this.endDate.set(endOfStartDate);

      if (this.selectedRange() !== EDateRangeType.custom) {
        this.setSelectedRange(EDateRangeType.custom);
      }

      this.syncPickerSelections();
    }

    if (startDate && endDate && isBefore(startOfDay(endDate), startOfDay(startDate))) {
      const endOfStartDate = endOfDay(startDate);
      this.form.get('endDate')?.setValue(endOfStartDate);
      this.endDate.set(endOfStartDate);

      if (this.selectedRange() !== EDateRangeType.custom) {
        this.setSelectedRange(EDateRangeType.custom);
      }

      this.syncPickerSelections();
    }
  }

  public onEndDateChange(date: Date | null): void {
    const validEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
      date,
      (this.form.get('startDate')?.value as Date | null) ?? null,
    );

    if (!validEndDate) {
      return;
    }

    // The end datepicker's [matDatepicker] input can't also take a [max] binding here without
    // breaking Angular's generic type inference for MatDatepickerInput, so the upper bound is
    // enforced here instead of on the calendar itself.
    const maxDate = this.allowedMaxDate();
    const clampedEndDate = maxDate && isBefore(maxDate, validEndDate) ? maxDate : validEndDate;

    this.form.get('endDate')?.setValue(clampedEndDate);
  }

  public onCompareEndDateChange(date: Date | null): void {
    const validEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
      date,
      (this.compareForm.get('startDate')?.value as Date | null) ?? null,
    );

    if (!validEndDate) {
      return;
    }

    const maxDate = this.allowedMaxDate();
    const clampedEndDate = maxDate && isBefore(maxDate, validEndDate) ? maxDate : validEndDate;

    this.compareForm.get('endDate')?.setValue(clampedEndDate);
  }

  private setupInitialDateRangeClampEffect(): void {
    effect(() => {
      const allTimeBounds = this.effectiveAllTimeBounds();
      const selectedRange = this.selectedRange();
      const shouldRunAutoClamp = this.shouldAutoClampInitialAllTime || this.pendingAllTimeClampEmission;

      if (
        selectedRange === EDateRangeType.allTime &&
        allTimeBounds &&
        shouldRunAutoClamp &&
        (this.pendingAllTimeClampEmission || !this.dateRangeIsClamped)
      ) {
        this.dateRangeIsClamped = true;

        // Captured before being reset below - distinguishes "the user explicitly picked 'Full
        // period' before bounds were ready" from the component's own initial default landing on
        // 'Full period', which should never emit on its own.
        const wasPendingUserSelection = this.pendingAllTimeClampEmission;

        const { end, start } = this.calculatePredefinedRange(EDateRangeType.allTime);
        const clampedDateRange = this.clampDateRangeToCampaign(start, end);

        this.isSettingPredefinedRange.set(true);
        this.startDate.set(clampedDateRange.startDate);
        this.endDate.set(clampedDateRange.endDate);

        // See `setupFormSync` - same reentrancy hazard as emitDateRange()'s combined patch.
        this.isSyncingFormFromSignals = true;

        try {
          this.form.patchValue(
            {
              endDate: clampedDateRange.endDate,
              startDate: clampedDateRange.startDate,
            },
            { emitEvent: false },
          );
        } finally {
          this.isSyncingFormFromSignals = false;
        }

        this.syncPickerSelections();

        // Don't emit here - like every other predefined range, "Full period" should only apply
        // once the menu closes (onMenuClosed -> emitDateRange), not the instant it's selected.
        this.pendingAllTimeClampEmission = false;
        this.shouldAutoClampInitialAllTime = false;

        // ...unless this was an explicit user selection and the menu already closed before
        // `allTimeBounds` arrived (e.g. the user picked "Full period" and closed the menu before
        // the campaign's bounds had loaded) - in that case `onMenuClosed` already emitted with the
        // old, unclamped dates, and nothing else will ever emit this correction on its own.
        if (wasPendingUserSelection && !this.isMenuOpen) {
          this.emitDateRange();
        }

        setTimeout(() => {
          this.isSettingPredefinedRange.set(false);
        });
      }
    });
  }

  private setupAllTimeRangeSyncEffect(): void {
    effect(() => {
      const allTimeBounds = this.effectiveAllTimeBounds();
      const selectedRange = this.selectedRange();

      if (this.isSettingPredefinedRange()) {
        return;
      }

      if (selectedRange !== EDateRangeType.allTime || !allTimeBounds) {
        return;
      }

      // Must match the exact startOfDay/endOfDay-normalized target `calculatePredefinedRange`
      // (and, downstream, `emitDateRange`) compute for `allTime` - not the raw, un-normalized
      // bounds. `clampDateRangeToCampaign(allTimeBounds.start, allTimeBounds.end)` used to be
      // called here, but clamping the bounds against themselves is always a no-op, so it just
      // returned the raw bounds verbatim. Since a real campaign's end is essentially never
      // exactly 23:59:59.999, that made `endDateChanged` below always true: this effect would
      // "correct" the range every run, `emitDateRange()` would immediately re-normalize and
      // write the value straight back, and the resulting isSettingPredefinedRange true->false
      // `setTimeout` would re-arm this same effect forever - a self-sustaining loop that ran
      // even while completely idle.
      const { end: clampedEndDate, start: clampedStartDate } = this.calculatePredefinedRange(EDateRangeType.allTime);

      const currentStartDate = this.startDate();
      const currentEndDate = this.endDate();
      const startDateChanged = !currentStartDate || currentStartDate.getTime() !== clampedStartDate.getTime();
      const endDateChanged = !currentEndDate || currentEndDate.getTime() !== clampedEndDate.getTime();

      if (!startDateChanged && !endDateChanged) {
        return;
      }

      this.isSettingPredefinedRange.set(true);
      this.startDate.set(clampedStartDate);
      this.endDate.set(clampedEndDate);

      // See `setupFormSync` - same reentrancy hazard as emitDateRange()'s combined patch.
      this.isSyncingFormFromSignals = true;

      try {
        this.form.patchValue(
          {
            endDate: clampedEndDate,
            startDate: clampedStartDate,
          },
          { emitEvent: false },
        );
      } finally {
        this.isSyncingFormFromSignals = false;
      }

      this.syncPickerSelections();

      // The campaign's start/end can change after the initial all-time clamp already ran
      // (e.g. the campaign gets edited while this page is open) - make sure the corrected
      // range is actually sent, not just reflected in the picker. The initial clamp itself
      // is already emitted by setupInitialDateRangeClampEffect, so this only needs to cover
      // changes that happen afterwards.
      if (this.dateRangeIsClamped) {
        this.emitDateRange();
      }

      setTimeout(() => {
        this.isSettingPredefinedRange.set(false);
      });
    });
  }

  private clampDateRangeToCampaign(
    startDate: Date | null,
    endDate: Date | null,
  ): Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'> {
    const bounds = this.effectiveAllTimeBounds();
    let clampedStartDate = startDate;
    let clampedEndDate = endDate;

    if (!bounds) {
      return { endDate: clampedEndDate, startDate: clampedStartDate };
    }

    const campaignStartDate = startOfDay(bounds.start);
    const campaignEndDate = endOfDay(bounds.end);

    if (clampedStartDate && isBefore(clampedStartDate, campaignStartDate)) {
      clampedStartDate = campaignStartDate;
    }

    if (clampedEndDate && isBefore(campaignEndDate, clampedEndDate)) {
      clampedEndDate = campaignEndDate;
    }

    if (clampedStartDate && clampedEndDate && isBefore(clampedEndDate, clampedStartDate)) {
      clampedStartDate = campaignStartDate;
      clampedEndDate = campaignEndDate;
    }

    return { endDate: clampedEndDate, startDate: clampedStartDate };
  }

  /**
   * Keeps "Previous period" in sync whenever the main range changes, and force-clears Compare on
   * "Full period". Handled as a single effect (rather than two separate ones) so there's no
   * ordering race between "clear because disabled" and "resync previous period", and so every
   * signal this depends on (including `compareRangeType`) is read unconditionally on every run -
   * a conditionally-read dependency (behind a short-circuited `&&`) would otherwise get silently
   * dropped from the dependency list on runs where it isn't reached.
   */
  private setupCompareStateEffect(): void {
    effect(() => {
      this.startDate();
      this.endDate();

      const compareRangeType = this.compareRangeType();

      if (this.compareDisabled()) {
        if (compareRangeType !== ECompareRangeType.none) {
          this.compareRangeType.set(ECompareRangeType.none);
          this.compareStartDate.set(null);
          this.compareEndDate.set(null);
          this.compareForm.patchValue({ endDate: null, startDate: null });
        }

        return;
      }

      if (compareRangeType === ECompareRangeType.previousPeriod) {
        this.applyPreviousPeriodCompareRange();
      }
    });
  }

  // Disables compareForm outside "Custom" mode - not just cosmetic: the matStartDate/matEndDate
  // inputs are visually hidden but still exist in the DOM, so without this a keyboard user could
  // tab into them and edit dates that then leak into the "previous period"/"none" preview.
  private setupCompareFormDisabledEffect(): void {
    effect(() => {
      if (this.compareDisabled() || this.compareRangeType() !== ECompareRangeType.custom) {
        this.compareForm.disable({ emitEvent: false });
      } else {
        this.compareForm.enable({ emitEvent: false });
      }
    });
  }

  private setupCompareFormValueChanges(): void {
    this.compareForm
      .get('startDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // See `applyPreviousPeriodCompareRange` - a value change here can be a stale echo
        // cascaded from Angular Material's linked start/end date-range inputs cross-validating
        // each other during our own programmatic signal->form sync, not a real form/user-driven
        // change.
        if (this.isSyncingCompareFormFromSignals) {
          return;
        }

        this.compareStartDate.set(value);
      });

    this.compareForm
      .get('endDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // See `applyPreviousPeriodCompareRange` - same stale-echo hazard as the startDate
        // subscriber above.
        if (this.isSyncingCompareFormFromSignals) {
          return;
        }

        const normalizedEndDate = this.datePickerUtilities.getValidEndDate(
          value as Date | null,
          (this.compareForm.get('startDate')?.value as Date | null) ?? null,
        );

        if (value && normalizedEndDate && value.getTime() !== normalizedEndDate.getTime()) {
          this.compareForm.get('endDate')?.setValue(normalizedEndDate, { emitEvent: false });
        }

        this.compareEndDate.set(normalizedEndDate);

        if (this.compareRangeType() === ECompareRangeType.custom) {
          this.emitCompareRange();
        }
      });
  }

  private applyPreviousPeriodCompareRange(): void {
    const mainStart = this.startDate();
    const mainEnd = this.endDate();

    if (!mainStart || !mainEnd) {
      return;
    }

    const { endDate: end, startDate: start } = this.computePreviousPeriod(mainStart, mainEnd);

    this.compareStartDate.set(start);
    this.compareEndDate.set(end);

    // See `syncPickerSelections`/`setupFormSync` - `compareForm`'s startDate/endDate are also a
    // linked Material date-range pair, so this patch can cross-trigger the same stale-echo
    // cascade back through `setupCompareFormValueChanges`'s subscribers.
    this.isSyncingCompareFormFromSignals = true;

    try {
      this.compareForm.patchValue({ endDate: end, startDate: start }, { emitEvent: false });
    } finally {
      this.isSyncingCompareFormFromSignals = false;
    }
  }

  /** The period immediately preceding `start`, of the same (inclusive) length as `[start, end]`. */
  private computePreviousPeriod(start: Date, end: Date): ICompareRange {
    const durationInDays = differenceInDays(startOfDay(end), startOfDay(start)) + 1;
    const previousEnd = endOfDay(subDays(startOfDay(start), 1));
    const previousStart = startOfDay(subDays(previousEnd, durationInDays - 1));

    return { endDate: previousEnd, startDate: previousStart };
  }

  private emitCompareRange(): void {
    if (this.compareDisabled() || this.compareRangeType() === ECompareRangeType.none) {
      if (this.lastEmittedCompareRange !== null) {
        this.lastEmittedCompareRange = null;
        this.compareRangeChanged.emit(null);
      }

      return;
    }

    const startDate = this.compareStartDate();
    const endDate = this.compareEndDate();

    if (!startDate || !endDate) {
      return;
    }

    const nextCompareRange: ICompareRange = { endDate: endOfDay(endDate), startDate: startOfDay(startDate) };

    if (
      nextCompareRange.startDate.getTime() === this.lastEmittedCompareRange?.startDate?.getTime() &&
      nextCompareRange.endDate.getTime() === this.lastEmittedCompareRange?.endDate?.getTime()
    ) {
      return;
    }

    this.lastEmittedCompareRange = nextCompareRange;
    this.compareRangeChanged.emit(nextCompareRange);
  }

  private setSelectedRange(range: EDateRangeType, emitEvent = true): void {
    if (this.selectedRange() === range) {
      return;
    }

    this.selectedRange.set(range);

    if (emitEvent) {
      this.selectedRangeChanged.emit(range);
    }
  }
}
